/**
 * Modal de troca obrigatória de senha.
 *
 * Aparece quando o usuário tem `must_change_password=true` (ex: após criação
 * de conta com senha temporária ou após reset de senha pelo Gestor Master).
 * Não pode ser fechado sem trocar a senha — protege contra credenciais
 * temporárias permanecerem ativas indefinidamente.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

export default function MustChangePasswordGate() {
  const { user, refresh, logout } = useAuth();
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user || !user.must_change_password) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (newPwd.length < 8) {
      toast.error("A nova senha precisa ter pelo menos 8 caracteres");
      return;
    }
    if (newPwd === oldPwd) {
      toast.error("A nova senha não pode ser igual à senha temporária");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("Senhas não conferem");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPwd, new_password: newPwd });
      toast.success("Senha atualizada! Use ela nos próximos acessos.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao trocar senha");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => { /* impede fechar */ }}>
      <DialogContent
        className="max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <ShieldCheck className="w-5 h-5 text-[hsl(var(--accent))]" />
            Defina uma nova senha pessoal
          </DialogTitle>
          <DialogDescription>
            Olá, <strong>{user.full_name}</strong>. Sua conta está com uma senha temporária. Por segurança, escolha uma nova senha pessoal agora para continuar.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Senha atual (temporária)</Label>
            <Input
              type={showPwd ? "text" : "password"}
              value={oldPwd}
              onChange={(e) => setOldPwd(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="must-change-old"
            />
          </div>
          <div>
            <Label>Nova senha (mínimo 8 caracteres)</Label>
            <Input
              type={showPwd ? "text" : "password"}
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="must-change-new"
            />
          </div>
          <div>
            <Label>Confirmar nova senha</Label>
            <Input
              type={showPwd ? "text" : "password"}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="must-change-confirm"
            />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              className="text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showPwd ? "Ocultar senhas" : "Mostrar senhas"}
            </button>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={logout} className="flex-1">
              Sair
            </Button>
            <Button type="submit" disabled={saving} className="flex-1" data-testid="must-change-submit">
              <KeyRound className="w-4 h-4 mr-1.5" />
              {saving ? "Salvando…" : "Salvar nova senha"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
